import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiDownload,
  FiEdit2,
  FiFileText,
  FiLoader,
  FiPackage,
  FiRefreshCw,
  FiTrash2,
  FiUpload,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const typeIcons = {
  BRAND: FiPackage,
  CATEGORY: FiPackage,
  SUBCATEGORY: FiPackage,
  COLOR: FiPackage,
  SIZE_GROUP: FiPackage,
  SIZE: FiPackage,
  UNIT: FiPackage,
  SEASON: FiPackage,
  HSN_CODE: FiFileText,
  GST_SLAB: FiFileText,
  SUPPLIER: FiPackage,
  BRANCH: FiPackage,
  DEPARTMENT: FiPackage,
  DESIGNATION: FiPackage,
  PRODUCT: FiPackage,
  OPENING_STOCK: FiPackage,
  VARIANT_STORE_SETTING: FiPackage,
  PRODUCT_SUPPLIER: FiPackage,
  CUSTOMER: FiPackage,
  CUSTOMER_ADDRESS: FiFileText,
  CUSTOMER_LOYALTY_OPENING: FiFileText,
  EMPLOYEE: FiUsers,
  EMPLOYEE_LEAVE_BALANCE: FiFileText,
  LEAVE_TYPE: FiFileText,
  ATTENDANCE_HOLIDAY_POLICY: FiFileText,
  EMPLOYEE_HOLIDAY_POLICY_ASSIGNMENT: FiUsers,
};

const displayName = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const rowData = (row) => row.normalizedData || row.rawData || {};

export default function ImportCenterPage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [catalog, setCatalog] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const [catalogResponse, jobsResponse] = await Promise.all([
        apiRequest("/imports/catalog", { accessToken }),
        apiRequest("/imports/jobs", { accessToken }),
      ]);
      setCatalog(catalogResponse || []);
      setJobs(jobsResponse || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load Import Centre.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const requestedType = searchParams.get("type");
    if (requestedType && catalog.some((item) => item.type === requestedType)) {
      setSelectedType(requestedType);
    }
  }, [catalog, searchParams]);

  const totals = useMemo(
    () => ({
      jobs: jobs.length,
      completed: jobs.filter((job) => job.status === "COMPLETED").length,
      rows: jobs.reduce((sum, job) => sum + Number(job.totalRows || 0), 0),
      needsReview: jobs.filter((job) =>
        ["VALIDATING", "READY_TO_CONFIRM"].includes(job.status),
      ).length,
    }),
    [jobs],
  );

  const openJob = async (jobId) => {
    navigate(`/admindashboard/tools/importcenter/review/${jobId}`);
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/imports/templates/${type}`,
        {
          credentials: "include",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) throw new Error("Unable to download template.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type.toLowerCase()}-import-template.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message || "Unable to download template.");
    }
  };

  const startUpload = (type) => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedType) return;

    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("type", selectedType);
      formData.append("file", file);
      const job = await apiRequest("/imports/preview", {
        method: "POST",
        body: formData,
        accessToken,
      });
      await load();
      navigate(`/admindashboard/tools/importcenter/review/${job.id}`);
    } catch (requestError) {
      setError(requestError.message || "Unable to preview this spreadsheet.");
    } finally {
      setUploading(false);
      setSelectedType(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <FiLoader className="animate-spin text-primary-800" size={30} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={uploadFile}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm text-slate-500">Tools / Import Centre</p>
          <h1 className="mt-1 text-3xl font-bold text-slate-950">
            Import Centre
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Upload an Excel or CSV template, correct every row in the preview,
            then confirm the import. No live data changes until confirmation.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {error ? (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
          <button className="ml-auto" onClick={() => setError("")}>
            <FiX />
          </button>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat title="Import jobs" value={totals.jobs} />
        <Stat title="Rows staged" value={totals.rows.toLocaleString()} />
        <Stat title="Completed" value={totals.completed} />
        <Stat
          title="Needs review"
          value={totals.needsReview}
          tone={totals.needsReview ? "amber" : "green"}
        />
      </div>

      <section>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-950">
            Choose data to import
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Templates use readable codes instead of technical database IDs.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {catalog.map((item) => {
            const Icon = typeIcons[item.type] || FiPackage;
            return (
              <div
                key={item.type}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-800">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-bold text-slate-950">{item.label}</h3>
                <p className="mt-2 min-h-10 text-sm leading-5 text-slate-500">
                  {item.description}
                </p>
                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => downloadTemplate(item.type)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <FiDownload /> Template
                  </button>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => startUpload(item.type)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-800 px-3 py-2.5 text-sm font-semibold text-white hover:bg-primary-900 disabled:opacity-50"
                  >
                    <FiUpload /> Import
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-100 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-slate-950">Recent imports</h2>
            <p className="mt-1 text-sm text-slate-500">
              Open a staged job to continue editing or confirm it.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-4">File</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Uploaded by</th>
                <th className="px-6 py-4">Rows</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody>
              {jobs.length ? (
                jobs.map((job) => (
                  <tr key={job.id} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-medium text-slate-800">
                      {job.originalFileName}
                    </td>
                    <td className="px-6 py-4">{displayName(job.type)}</td>
                    <td className="px-6 py-4 text-slate-500">
                      {job.createdBy?.employee?.fullName ||
                        job.createdBy?.email ||
                        "—"}
                    </td>
                    <td className="px-6 py-4">{job.totalRows}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openJob(job.id)}
                        className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-bold text-primary-800 hover:bg-primary-100"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className="px-6 py-10 text-center text-slate-500"
                    colSpan="6"
                  >
                    No import jobs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function ImportPreviewModal({ job, accessToken, onClose, onJobChange }) {
  const [editingRow, setEditingRow] = useState(null);
  const [temporaryCredentials, setTemporaryCredentials] = useState([]);
  const [referenceLookups, setReferenceLookups] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const headers = Array.isArray(job.headers) ? job.headers : [];
  const activeRows = job.rows?.filter((row) => row.status !== "DELETED") || [];
  const canEdit = [
    "VALIDATING",
    "READY_TO_CONFIRM",
    "COMPLETED_WITH_ERRORS",
  ].includes(job.status);

  useEffect(() => {
    let active = true;

    apiRequest(`/imports/lookups/${job.type}`, { accessToken })
      .then((lookups) => {
        if (!active) return;
        setReferenceLookups(
          Object.fromEntries(
            (lookups || []).map((lookup) => [lookup.field, lookup]),
          ),
        );
      })
      .catch(() => {
        if (active) setReferenceLookups({});
      });

    return () => {
      active = false;
    };
  }, [accessToken, job.type]);

  const request = async (path, options) => {
    setSaving(true);
    setError("");
    try {
      const updated = await apiRequest(path, { accessToken, ...options });
      if (Array.isArray(updated?.temporaryCredentials)) {
        setTemporaryCredentials(updated.temporaryCredentials);
      }
      onJobChange(updated);
      return updated;
    } catch (requestError) {
      setError(requestError.message || "Import action failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveRow = async () => {
    const updated = await request(
      `/imports/jobs/${job.id}/rows/${editingRow.rowNumber}`,
      { method: "PATCH", body: { data: editingRow.data } },
    );
    if (updated) setEditingRow(null);
  };
  const deleteRow = (row) =>
    request(`/imports/jobs/${job.id}/rows/${row.rowNumber}`, {
      method: "DELETE",
    });
  const confirm = async () => {
    if (
      !window.confirm(
        `Import ${job.readyRows + job.warningRows} reviewed row(s) into live ${displayName(job.type)} data?`,
      )
    )
      return;
    await request(`/imports/jobs/${job.id}/confirm`, { method: "POST" });
  };

  const downloadTemporaryCredentials = () => {
    const escapeCsv = (value) =>
      `"${String(value ?? "").replaceAll('"', '""')}"`;
    const rows = [
      [
        "Employee code",
        "Employee name",
        "Email",
        "Phone",
        "Temporary password",
      ],
      ...temporaryCredentials.map((credential) => [
        credential.employeeCode,
        credential.fullName,
        credential.email,
        credential.phone,
        credential.temporaryPassword,
      ]),
    ];
    const blob = new Blob(
      [rows.map((row) => row.map(escapeCsv).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `charms-employee-temporary-credentials-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-800">
              {displayName(job.type)} import preview
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-950">
              {job.originalFileName}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Edit or remove any row before confirmation. Errors must be
              resolved first.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="grid gap-3 border-b border-slate-100 bg-slate-50 px-6 py-4 sm:grid-cols-4">
          <MiniStat label="Ready" value={job.readyRows} tone="green" />
          <MiniStat label="Warnings" value={job.warningRows} tone="amber" />
          <MiniStat label="Errors" value={job.errorRows} tone="red" />
          <MiniStat label="Removed" value={job.skippedRows} />
        </div>
        {error ? (
          <div className="mx-6 mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        {temporaryCredentials.length ? (
          <div className="mx-6 mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-bold">
                  Save temporary employee credentials now
                </p>
                <p className="mt-1 leading-5 text-amber-800">
                  These passwords are shown only for this confirmation. They are
                  not saved in the import history and cannot be recovered later.
                </p>
              </div>
              <button
                type="button"
                onClick={downloadTemporaryCredentials}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-900 px-3 py-2 text-xs font-bold text-white hover:bg-amber-950"
              >
                <FiDownload /> Download CSV
              </button>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-amber-200 bg-white">
              <table className="w-full min-w-[720px] text-left text-xs">
                <thead className="bg-amber-100 text-amber-950">
                  <tr>
                    <th className="px-3 py-2.5">Employee</th>
                    <th className="px-3 py-2.5">Contact</th>
                    <th className="px-3 py-2.5">Temporary password</th>
                  </tr>
                </thead>
                <tbody>
                  {temporaryCredentials.map((credential) => (
                    <tr
                      key={`${credential.employeeCode}-${credential.rowNumber}`}
                      className="border-t border-amber-100"
                    >
                      <td className="px-3 py-2.5 font-semibold">
                        {credential.fullName} · {credential.employeeCode}
                      </td>
                      <td className="px-3 py-2.5">
                        {credential.email || credential.phone || "—"}
                      </td>
                      <td className="px-3 py-2.5 font-mono font-bold tracking-wide">
                        {credential.temporaryPassword}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Row</th>
                <th className="px-4 py-3">Status</th>
                {headers.map((header) => (
                  <th key={header} className="px-4 py-3">
                    {header}
                  </th>
                ))}
                <th className="px-4 py-3">Issues</th>
                {canEdit ? <th className="px-4 py-3" /> : null}
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => {
                const data = rowData(row);
                return (
                  <tr
                    key={row.id}
                    className="border-t border-slate-100 align-top"
                  >
                    <td className="px-4 py-3 font-medium text-slate-600">
                      {row.rowNumber}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    {headers.map((header) => (
                      <td
                        key={header}
                        className="max-w-48 px-4 py-3 text-slate-700"
                      >
                        {String(data[header] ?? "—")}
                      </td>
                    ))}
                    <td className="max-w-72 px-4 py-3">
                      {Array.isArray(row.validation) &&
                      row.validation.length ? (
                        <ul className="space-y-1 text-xs text-red-700">
                          {row.validation.map((issue, index) => (
                            <li key={index}>
                              • {issue.field ? `${issue.field}: ` : ""}
                              {issue.message}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-xs text-green-700">Ready</span>
                      )}
                    </td>
                    {canEdit ? (
                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            setEditingRow({
                              rowNumber: row.rowNumber,
                              data: { ...data },
                            })
                          }
                          className="mr-2 rounded-lg bg-primary-50 p-2 text-primary-800 hover:bg-primary-100"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => deleteRow(row)}
                          className="rounded-lg bg-red-50 p-2 text-red-700 hover:bg-red-100"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={() =>
              request(`/imports/jobs/${job.id}/revalidate`, { method: "POST" })
            }
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            <FiRefreshCw /> Revalidate
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"
            >
              Close
            </button>
            <button
              type="button"
              disabled={!canEdit || job.errorRows > 0 || saving}
              onClick={confirm}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiCheckCircle /> Confirm import
            </button>
          </div>
        </div>
      </div>
      {editingRow ? (
        <RowEditor
          headers={headers}
          row={editingRow}
          referenceLookups={referenceLookups}
          saving={saving}
          onClose={() => setEditingRow(null)}
          onChange={setEditingRow}
          onSave={saveRow}
        />
      ) : null}
    </div>
  );
}

export function RowEditor({
  headers,
  row,
  referenceLookups,
  saving,
  onClose,
  onChange,
  onSave,
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-primary-800">
              Edit staged row
            </p>
            <h3 className="mt-1 text-xl font-bold">
              Spreadsheet row {row.rowNumber}
            </h3>
          </div>
          <button onClick={onClose} className="rounded-xl bg-slate-100 p-2">
            <FiX />
          </button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {headers.map((header) => (
            <label key={header} className="text-sm font-medium text-slate-700">
              {referenceLookups[header]?.label || header}
              <input
                list={
                  referenceLookups[header]
                    ? `import-lookup-${row.rowNumber}-${header}`
                    : undefined
                }
                value={row.data[header] ?? ""}
                onChange={(event) =>
                  onChange({
                    ...row,
                    data: { ...row.data, [header]: event.target.value },
                  })
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal outline-none focus:border-primary-700"
              />
              {referenceLookups[header] ? (
                <>
                  <datalist id={`import-lookup-${row.rowNumber}-${header}`}>
                    {referenceLookups[header].options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </datalist>
                  <span className="mt-1 block text-[11px] font-normal text-slate-500">
                    Search or type a{" "}
                    {referenceLookups[header].label?.toLowerCase()} code.
                  </span>
                </>
              ) : null}
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            disabled={saving}
            onClick={onSave}
            className="rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            Save and validate
          </button>
        </div>
      </div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = {
    READY: "bg-green-100 text-green-700",
    WARNING: "bg-amber-100 text-amber-700",
    ERROR: "bg-red-100 text-red-700",
    IMPORTED: "bg-blue-100 text-blue-700",
    DELETED: "bg-slate-100 text-slate-500",
    COMPLETED: "bg-green-100 text-green-700",
    READY_TO_CONFIRM: "bg-amber-100 text-amber-700",
    VALIDATING: "bg-amber-100 text-amber-700",
    IMPORTING: "bg-blue-100 text-blue-700",
    COMPLETED_WITH_ERRORS: "bg-red-100 text-red-700",
    CANCELLED: "bg-slate-100 text-slate-500",
    FAILED: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${map[status] || "bg-slate-100 text-slate-600"}`}
    >
      {displayName(status)}
    </span>
  );
}
function Stat({ title, value, tone = "primary" }) {
  const tones = {
    primary: "border-primary-100 bg-primary-50",
    amber: "border-amber-100 bg-amber-50",
    green: "border-green-100 bg-green-50",
  };
  return (
    <div className={`rounded-3xl border p-5 ${tones[tone] || tones.primary}`}>
      <p className="text-sm text-slate-500">{title}</p>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </div>
  );
}
function MiniStat({ label, value, tone = "slate" }) {
  const colors = {
    green: "text-green-700",
    amber: "text-amber-700",
    red: "text-red-700",
    slate: "text-slate-700",
  };
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1 text-xl font-bold ${colors[tone]}`}>{value || 0}</p>
    </div>
  );
}
